// TranscriptEditorPage chrome (toolbar, dialogs, tooltips, toasts, player
// controls) — NOT the per-word transcript-translation feature (its own
// fetched/cached content), and NOT the AI-review suggestion text itself,
// which both stay untouched. Merged into `translations` by ../i18n.js.
export const editor = {
  en: {
    editor: {
      toolbar: {
        back: "Back",
        backToTranscriptions: "Back to transcriptions",
        fileActions: "File actions",
        rename: "Rename",
        delete: "Delete",
        statusLocked: "Editing locked while AI job runs",
        statusSaving: "Saving…",
        statusUnsaved: "Unsaved changes",
        statusSaved: "All changes saved",
        saveChanges: "Save changes",
        resizeLines: "Resize transcript lines",
        wordsPerLine: "Words per line",
        search: "Search transcript",
        findPlaceholder: "Find in transcript",
        previousMatch: "Previous match",
        nextMatch: "Next match",
        matchesHint: "Matches are highlighted in the transcript.",
        copyTranscript: "Copy transcript",
        translationMenu: "Translation",
        translatingEllipsis: "Translating…",
        saveChangesFirst: "Save changes first",
        retranslate: "Retranslate",
        translate: "Translate",
        view: "View",
        aiReviewMenu: "AI Review",
        reviewWithAI: "Review with AI",
        hideReview: "Hide review",
        viewReview: "View review",
        regenerate: "Regenerate",
        export: "Export",
        exportVideo: "Video",
        aiCorrectionsCount: (n) => `${n} AI correction${n === 1 ? "" : "s"}`,
        acceptAll: "Accept all",
        rejectAll: "Reject all",
      },
      player: {
        enterFullscreen: "Enter fullscreen",
        exitFullscreen: "Exit fullscreen",
        seekVideo: "Seek video",
        seek: "Seek",
        playVideo: "Play video",
        pauseVideo: "Pause video",
        changeSpeed: "Change playback speed",
        jumpToCurrentWord: "Jump to current word",
        play: "Play",
        pause: "Pause",
        back5: "Back 5 seconds",
        forward5: "Forward 5 seconds",
        mediaVideo: "video",
        mediaAudio: "audio",
      },
      lineActions: {
        editWord: "Edit a word",
        splitLine: "Split this line",
        mergeAbove: "Merge with line above",
        mergeBelow: "Merge with line below",
        acceptCorrection: "Accept correction",
        rejectCorrection: "Reject correction",
        editTranslatedWord: "Edit a translated word",
        splitTranslatedLine: "Split this translated line",
        mergeTranslatedAbove: "Merge with translated line above",
        mergeTranslatedBelow: "Merge with translated line below",
        savingTranslation: "Saving translation…",
        saveTranslationChanges: "Save translation changes",
        titleLocked: "Click to jump there; editing is unavailable while an AI job is running",
        titleEditWord: "Click to edit this word",
        titleChooseLaterWord: "Choose a later word to split here",
        titleSplitBeforeWord: "Click to split the line before this word",
        titleAiSuggests: (word) => `AI suggests "${word}"`,
        titleDefault: "Click to jump there, double-click to edit",
        titleEditTranslatedWord: "Click to edit this translated word",
        titleSplitTranslatedBeforeWord: "Click to split the translated line before this word",
        titleDefaultTranslation: "Click to jump there, double-click to edit translation",
        chooseWordAfterFirst: "Choose a word after the first one to split there.",
      },
      dialogs: {
        renameTitle: "Rename transcription",
        nameLabel: "Name",
        save: "Save",
        cancel: "Cancel",
        deleteTitle: "Delete transcription",
        deleteDescription: "This will permanently delete this transcription. This action cannot be undone.",
        deleteConfirm: "Delete",
        confirm: "Confirm",
        creditMinutes: (minutes, value) => `${minutes} ${value === 1 ? "minute" : "minutes"}`,
        translationCreditTitle: (creditCost) => `Use ${creditCost} of credit for translation?`,
        translationCreditDescription: (creditCost) =>
          `This translation will consume ${creditCost} from your credit balance. Retranslating is charged separately.`,
        reviewCreditTitle: (creditCost) => `Use ${creditCost} of credit for AI review?`,
        reviewCreditDescription: (creditCost) =>
          `This AI review will consume ${creditCost} from your credit balance. Regenerating is charged separately.`,
        resizeTitle: "Update segmentation",
        resizeDescription: (n) =>
          `This will regroup the transcript into lines of ${n} word${n === 1 ? "" : "s"} each and save immediately.`,
        acceptAllTitle: "Accept all corrections",
        rejectAllTitle: "Reject all corrections",
        acceptAllDescription: (n) =>
          `This will apply all ${n} suggested correction${n === 1 ? "" : "s"} to the transcript.`,
        rejectAllDescription: (n) =>
          `This will dismiss all ${n} suggested correction${n === 1 ? "" : "s"}. This can't be undone.`,
      },
      toasts: {
        cantEnterFullscreen: "Couldn't enter fullscreen.",
        translateTimeout: (langName) => `Translation to ${langName} is taking too long — stopped. Try again.`,
        translated: (langName) => `Translated to ${langName}`,
        translateFailed: (langName) => `Translation to ${langName} failed.`,
        reviewFoundCount: (n) => `AI review found ${n} correction${n === 1 ? "" : "s"}`,
        reviewFoundNothing: "AI review found nothing to fix",
        reviewTimeout: "AI review is taking too long — stopped. Try again.",
        reviewFailed: "AI review failed.",
        videoExportTimeout: "Video export is taking too long — stopped. Try again.",
        videoExportReady: "Video export ready",
        videoExportFailed: "Video export failed.",
        cantPlayMedia: (mediaLabel) => `Couldn't play this ${mediaLabel}.`,
        cantLoadMedia: (mediaLabel) => `Couldn't load the ${mediaLabel} for this transcript.`,
        changesSaved: "Changes saved",
        changesFailedCount: (n) => `${n} changes couldn't be saved.`,
        saveTranslationsBeforeResize: "Save translation changes before resizing transcript lines.",
        segmentationUpdated: "Segmentation updated",
        transcriptionDeleted: "Transcription deleted",
        transcriptCopied: "Transcript copied to clipboard",
        clipboardUnavailable: "Could not reach the clipboard from this browser.",
        translatingTo: (langName) => `Translating to ${langName}…`,
        saveBeforeTranslate: "Save your changes before starting a translation.",
        saveBeforeReview: "Save your changes before starting an AI review.",
        correctionsFailed: (n, statusWord) => `${n} correction${n === 1 ? "" : "s"} couldn't be ${statusWord}.`,
        statusAccepted: "accepted",
        statusRejected: "rejected",
        allAccepted: "All corrections accepted",
        allRejected: "All corrections rejected",
      },
      states: {
        failedGeneric: "This transcription failed.",
        transcribingVideo: "Transcribing your video",
        transcribingAudio: "Transcribing your audio",
      },
    },
  },
  ar: {
    editor: {
      toolbar: {
        back: "رجوع",
        backToTranscriptions: "الرجوع إلى النصوص المفرّغة",
        fileActions: "إجراءات الملف",
        rename: "إعادة تسمية",
        delete: "حذف",
        statusLocked: "التحرير مقفل أثناء تشغيل مهمة الذكاء الاصطناعي",
        statusSaving: "جارٍ الحفظ…",
        statusUnsaved: "تغييرات غير محفوظة",
        statusSaved: "تم حفظ جميع التغييرات",
        saveChanges: "حفظ التغييرات",
        resizeLines: "تغيير تقسيم أسطر النص المفرّغ",
        wordsPerLine: "كلمات لكل سطر",
        search: "البحث في النص المفرّغ",
        findPlaceholder: "بحث في النص المفرّغ",
        previousMatch: "التطابق السابق",
        nextMatch: "التطابق التالي",
        matchesHint: "يتم تمييز التطابقات في النص المفرّغ.",
        copyTranscript: "نسخ النص",
        translationMenu: "الترجمة",
        translatingEllipsis: "جارٍ الترجمة…",
        saveChangesFirst: "احفظ التغييرات أولاً",
        retranslate: "إعادة الترجمة",
        translate: "ترجمة",
        view: "عرض",
        aiReviewMenu: "مراجعة الذكاء الاصطناعي",
        reviewWithAI: "مراجعة بالذكاء الاصطناعي",
        hideReview: "إخفاء المراجعة",
        viewReview: "عرض المراجعة",
        regenerate: "إعادة الإنشاء",
        export: "تصدير",
        exportVideo: "الفيديو",
        aiCorrectionsCount: (n) => `${n} تصحيح من الذكاء الاصطناعي`,
        acceptAll: "قبول الكل",
        rejectAll: "رفض الكل",
      },
      player: {
        enterFullscreen: "الدخول في وضع ملء الشاشة",
        exitFullscreen: "الخروج من وضع ملء الشاشة",
        seekVideo: "الانتقال ضمن الفيديو",
        seek: "الانتقال ضمن الشريط الزمني",
        playVideo: "تشغيل الفيديو",
        pauseVideo: "إيقاف الفيديو مؤقتًا",
        changeSpeed: "تغيير سرعة التشغيل",
        jumpToCurrentWord: "الانتقال إلى الكلمة الحالية",
        play: "تشغيل",
        pause: "إيقاف مؤقت",
        back5: "الرجوع 5 ثوانٍ",
        forward5: "التقدّم 5 ثوانٍ",
        mediaVideo: "الفيديو",
        mediaAudio: "الصوت",
      },
      lineActions: {
        editWord: "تعديل كلمة",
        splitLine: "تقسيم هذا السطر",
        mergeAbove: "دمج مع السطر أعلاه",
        mergeBelow: "دمج مع السطر أدناه",
        acceptCorrection: "قبول التصحيح",
        rejectCorrection: "رفض التصحيح",
        editTranslatedWord: "تعديل كلمة مترجمة",
        splitTranslatedLine: "تقسيم هذا السطر المترجم",
        mergeTranslatedAbove: "دمج مع السطر المترجم أعلاه",
        mergeTranslatedBelow: "دمج مع السطر المترجم أدناه",
        savingTranslation: "جارٍ حفظ الترجمة…",
        saveTranslationChanges: "حفظ تغييرات الترجمة",
        titleLocked: "انقر للانتقال إلى هناك؛ التعديل غير متاح أثناء تشغيل مهمة الذكاء الاصطناعي",
        titleEditWord: "انقر لتعديل هذه الكلمة",
        titleChooseLaterWord: "اختر كلمة لاحقة للتقسيم هنا",
        titleSplitBeforeWord: "انقر لتقسيم السطر قبل هذه الكلمة",
        titleAiSuggests: (word) => `يقترح الذكاء الاصطناعي "${word}"`,
        titleDefault: "انقر للانتقال إلى هناك، وانقر مرتين للتعديل",
        titleEditTranslatedWord: "انقر لتعديل هذه الكلمة المترجمة",
        titleSplitTranslatedBeforeWord: "انقر لتقسيم السطر المترجم قبل هذه الكلمة",
        titleDefaultTranslation: "انقر للانتقال إلى هناك، وانقر مرتين لتعديل الترجمة",
        chooseWordAfterFirst: "اختر كلمة بعد الكلمة الأولى للتقسيم هناك.",
      },
      dialogs: {
        renameTitle: "إعادة تسمية النص المفرّغ",
        nameLabel: "الاسم",
        save: "حفظ",
        cancel: "إلغاء",
        deleteTitle: "حذف النص المفرّغ",
        deleteDescription: "سيؤدي هذا إلى حذف هذا النص المفرّغ نهائيًا. لا يمكن التراجع عن هذا الإجراء.",
        deleteConfirm: "حذف",
        confirm: "تأكيد",
        creditMinutes: (minutes) => `${minutes} دقيقة`,
        translationCreditTitle: (creditCost) => `استخدام ${creditCost} من الرصيد للترجمة؟`,
        translationCreditDescription: (creditCost) =>
          `ستستهلك هذه الترجمة ${creditCost} من رصيدك. وتُحتسب إعادة الترجمة بشكل منفصل.`,
        reviewCreditTitle: (creditCost) => `استخدام ${creditCost} من الرصيد لمراجعة الذكاء الاصطناعي؟`,
        reviewCreditDescription: (creditCost) =>
          `ستستهلك مراجعة الذكاء الاصطناعي هذه ${creditCost} من رصيدك. وتُحتسب إعادة الإنشاء بشكل منفصل.`,
        resizeTitle: "تحديث التقسيم",
        resizeDescription: (n) => `سيُعاد تقسيم النص المفرّغ إلى أسطر من ${n} كلمة لكل سطر، وسيُحفظ ذلك فورًا.`,
        acceptAllTitle: "قبول جميع التصحيحات",
        rejectAllTitle: "رفض جميع التصحيحات",
        acceptAllDescription: (n) => `سيتم تطبيق جميع التصحيحات المقترحة (${n}) على النص المفرّغ.`,
        rejectAllDescription: (n) => `سيتم تجاهل جميع التصحيحات المقترحة (${n}). لا يمكن التراجع عن هذا الإجراء.`,
      },
      toasts: {
        cantEnterFullscreen: "تعذّر الدخول في وضع ملء الشاشة.",
        translateTimeout: (langName) => `الترجمة إلى ${langName} تستغرق وقتًا طويلاً — تم الإيقاف. حاول مرة أخرى.`,
        translated: (langName) => `تمت الترجمة إلى ${langName}`,
        translateFailed: (langName) => `فشلت الترجمة إلى ${langName}.`,
        reviewFoundCount: (n) => `عثرت مراجعة الذكاء الاصطناعي على ${n} تصحيح`,
        reviewFoundNothing: "لم تجد مراجعة الذكاء الاصطناعي أي شيء لإصلاحه",
        reviewTimeout: "مراجعة الذكاء الاصطناعي تستغرق وقتًا طويلاً — تم الإيقاف. حاول مرة أخرى.",
        reviewFailed: "فشلت مراجعة الذكاء الاصطناعي.",
        videoExportTimeout: "تصدير الفيديو يستغرق وقتًا طويلاً — تم الإيقاف. حاول مرة أخرى.",
        videoExportReady: "تصدير الفيديو جاهز",
        videoExportFailed: "فشل تصدير الفيديو.",
        cantPlayMedia: (mediaLabel) => `تعذّر تشغيل ${mediaLabel}.`,
        cantLoadMedia: (mediaLabel) => `تعذّر تحميل ${mediaLabel} لهذا النص المفرّغ.`,
        changesSaved: "تم حفظ التغييرات",
        changesFailedCount: (n) => `تعذّر حفظ ${n} تغيير.`,
        saveTranslationsBeforeResize: "احفظ تغييرات الترجمة قبل تغيير تقسيم أسطر النص.",
        segmentationUpdated: "تم تحديث التقسيم",
        transcriptionDeleted: "تم حذف النص المفرّغ",
        transcriptCopied: "تم نسخ النص إلى الحافظة",
        clipboardUnavailable: "تعذّر الوصول إلى الحافظة من هذا المتصفح.",
        translatingTo: (langName) => `جارٍ الترجمة إلى ${langName}…`,
        saveBeforeTranslate: "احفظ تغييراتك قبل بدء الترجمة.",
        saveBeforeReview: "احفظ تغييراتك قبل بدء مراجعة الذكاء الاصطناعي.",
        correctionsFailed: (n, statusWord) => `تعذّر ${statusWord} ${n} تصحيح.`,
        statusAccepted: "قبول",
        statusRejected: "رفض",
        allAccepted: "تم قبول جميع التصحيحات",
        allRejected: "تم رفض جميع التصحيحات",
      },
      states: {
        failedGeneric: "فشل التفريغ الصوتي.",
        transcribingVideo: "جارٍ تفريغ الفيديو",
        transcribingAudio: "جارٍ تفريغ الصوت",
      },
    },
  },
};
